// VerificationsPage — /verifications staff REVIEW surface (Phase 4, design §j).
//
// Lists application groups that have rental-verification records, with a
// status summary + red-flag count. Opening a group shows the detail modal:
// per-tenancy applicant-claimed vs. landlord-confirmed side by side, screening
// answers, red flags highlighted, skip reason (+ "Send anyway"), and Verified /
// Concerns verdict buttons. ADVISORY ONLY — nothing here blocks a lease send.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRealtime } from '@/hooks/useRealtime';
import { createPortal } from 'react-dom';
import {
  listVerifications,
  setVerdict,
  sendSkipped,
  editLandlord,
  resendVerification,
  clearRvFlag,
  type EditLandlordBody,
  type RentalVerificationRecord,
  type VerificationGroupApplicant,
  type VerificationGroupSummary,
} from '@/lib/verificationsClient';
import { matchesVerificationQuery, searchTokens } from '@/lib/verificationsSearch';
import { useFocusTrap } from '@/lib/useFocusTrap';
import { useEscapeKey } from '@/lib/useEscapeKey';
import { useInertBackground } from '@/lib/useInertBackground';

type PillTone = 'success' | 'warning' | 'danger' | 'info' | 'accent' | 'neutral';

const STATUS_META: Record<string, { label: string; tone: PillTone }> = {
  created: { label: 'Queued', tone: 'neutral' },
  skipped: { label: 'Skipped', tone: 'warning' },
  'whp-internal-review': { label: 'WHP — internal', tone: 'info' },
  'no-contact': { label: 'No contact', tone: 'warning' },
  sent: { label: 'Sent', tone: 'info' },
  reminded: { label: 'Reminded', tone: 'info' },
  responded: { label: 'Responded', tone: 'success' },
  declined: { label: 'Declined', tone: 'danger' },
  'denied-tenancy': { label: 'Denied tenancy', tone: 'danger' },
  'related-shortcut': { label: 'Related', tone: 'warning' },
  bounced: { label: 'Bounced', tone: 'danger' },
  'no-response': { label: 'No response', tone: 'neutral' },
};

function statusMeta(status: string): { label: string; tone: PillTone } {
  return STATUS_META[status] ?? { label: status, tone: 'neutral' };
}

// WEL-18: AppFolio application status label + pill tone — identical to the
// Applications tab (ListPage) so the Status column reads the same across tabs.
const AF_STATUS_LABEL: Record<string, string> = {
  approved: 'Approved',
  in_screening: 'In screening',
  info_needed: 'Info needed',
  in_review: 'In review',
  decision_pending: 'Decision pending',
  new: 'New',
  denied: 'Denied',
  canceled: 'Canceled',
};

const AF_STATUS_TONE: Record<string, PillTone> = {
  approved: 'success',
  in_screening: 'info',
  info_needed: 'warning',
  in_review: 'info',
  decision_pending: 'neutral',
  new: 'neutral',
  denied: 'danger',
  canceled: 'danger',
};

// WEL-18: "LeadName +N" applicant summary — mirrors ListPage.applicantSummary.
function applicantSummary(applicants: VerificationGroupApplicant[]): string {
  const primary = applicants.find((a) => a.isPrimary) ?? applicants[0];
  if (!primary) return '—';
  const lead = `${primary.firstName} ${primary.lastName}`.trim() || '—';
  if (applicants.length === 1) return lead;
  return `${lead} +${applicants.length - 1}`;
}

// WEL-63: Active / Past / All filter for the review queue. "Active" = the
// application/group is still active in AppFolio's pipeline (matches the
// Applications page's "active groups"); "Past" = no longer active (denied /
// cancelled / converted / aged-out). Default is Active — the most useful view,
// consistent with the Applications page.
type ScopeFilter = 'active' | 'past' | 'all';

const SCOPE_OPTIONS: Array<{ value: ScopeFilter; label: string }> = [
  { value: 'active', label: 'Active applications' },
  { value: 'past', label: 'Past applications' },
  { value: 'all', label: 'All applications' },
];

/** A group with no explicit `active` flag (legacy API) is treated as ACTIVE. */
function isActiveGroup(g: VerificationGroupSummary): boolean {
  return g.active !== false;
}

/**
 * Is the group currently color-flagged? = it's NOT completely clean
 * (`needsAttention`: any red flag OR a 'concerns' verdict) AND staff haven't yet
 * cleared it (`flagCleared !== true`). Flagged rows render the attention color;
 * clean or already-cleared rows render normally. (A missing `flagCleared` from a
 * legacy API counts as NOT cleared, so a not-clean group still flags.)
 */
function isFlagged(g: VerificationGroupSummary): boolean {
  return g.needsAttention && g.flagCleared !== true;
}

function matchesScope(g: VerificationGroupSummary, scope: ScopeFilter): boolean {
  if (scope === 'all') return true;
  if (scope === 'active') return isActiveGroup(g);
  return !isActiveGroup(g);
}

/** First non-blank value, treating '' and whitespace as absent — the mirror
 *  stores blank names, and `??` lets an empty string through. */
function firstText(...vals: Array<string | null | undefined>): string | null {
  for (const v of vals) {
    const t = (v ?? '').trim();
    if (t) return t;
  }
  return null;
}

function money(n: number | undefined): string {
  if (n === undefined) return '—';
  return `$${n.toLocaleString()}`;
}

function dateRange(from: string | undefined, to: string | undefined, current: boolean): string {
  const start = from || '?';
  const end = current ? 'present' : to || '?';
  if (!from && !to && !current) return '—';
  return `${start} → ${end}`;
}

function addressLine(r: RentalVerificationRecord): string {
  const a = r.claimed.address;
  const parts = [a.line1, a.line2, a.city, a.state, a.postalCode].filter(Boolean);
  return parts.join(', ') || a.line1 || '(no address)';
}

export function VerificationsPage() {
  const [groups, setGroups] = useState<VerificationGroupSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openGroupId, setOpenGroupId] = useState<string | null>(null);
  // WEL-63: Active / Past / All filter — default Active (consistent with the
  // Applications page; the most useful day-to-day view).
  const [scope, setScope] = useState<ScopeFilter>('active');
  // Find-one-row search: person · property/address · status (see haystack()).
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listVerifications();
      setGroups(res.groups);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load verifications');
    } finally {
      setLoading(false);
    }
  }, []);

  // Optimistically clear a group's color flag the instant staff click "Mark
  // reviewed" — flip flagCleared locally so the row de-colors immediately; the
  // refetch (onChanged → load) then confirms it from the server.
  const optimisticallyClearFlag = useCallback((groupId: string) => {
    setGroups((prev) =>
      prev.map((g) => (g.groupId === groupId ? { ...g, flagCleared: true } : g)),
    );
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Live push: since the 2026-07-14 mirror-resweep→change-drain cutover, the
  // change-drain is the realtime driver and it broadcasts `screen-changed` with
  // screen 'rental-verification' when a record re-syncs (changeDrain.ts) — NOT
  // the legacy `rental-verification` type the disabled resweep route emitted.
  // Listen for BOTH (legacy kept for the hourly/manual route + backward-compat)
  // and rehydrate on WS reconnect in case a push was missed. Mirrors ListPage's
  // handler. No-op until the WS API is wired (GET /api/ws/ticket returns url: null).
  useRealtime((msg) => {
    if (!msg || typeof msg !== 'object') return;
    const m = msg as { type?: string; screen?: string };
    if (m.type === '__ws_reconnect') void load();
    else if (m.type === 'rental-verification') void load();
    else if (m.type === 'screen-changed' && m.screen === 'rental-verification') void load();
  });

  const openGroup = useMemo(
    () => groups.find((g) => g.groupId === openGroupId) ?? null,
    [groups, openGroupId],
  );

  const tokens = useMemo(() => searchTokens(query), [query]);
  // WEL-63: rows visible under the current Active / Past / All filter, narrowed
  // by the search box.
  const visibleGroups = useMemo(
    () => groups.filter((g) => matchesScope(g, scope) && matchesVerificationQuery(g, tokens)),
    [groups, scope, tokens],
  );
  // Per-scope counts for the filter labels + the "N of M" summary.
  const activeCount = useMemo(() => groups.filter(isActiveGroup).length, [groups]);
  const pastCount = groups.length - activeCount;
  // How many rows the SCOPE alone would show — so the summary can say whether
  // the search is what's hiding things.
  const inScopeCount = useMemo(
    () => groups.filter((g) => matchesScope(g, scope)).length,
    [groups, scope],
  );

  return (
    // WEL-63 (optional): cap the content width + center it so the Verifications
    // page reads like a contained card, matching the Applications page (which
    // caps at 1200) rather than full-bleed.
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 'var(--space-7)' }}>
        <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--weight-bold)', color: 'var(--fg)', margin: 0 }}>
          Rental verifications
        </h1>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', marginTop: 'var(--space-2)' }}>
          Applicant-claimed vs. landlord-confirmed rental history. Advisory only — verdicts never block a lease send.
        </p>
      </div>

      {/* WEL-63: Active / Past / All filter — single obvious dropdown, default Active. */}
      {!loading && !error && groups.length > 0 && (
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-4)',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            marginBottom: 'var(--space-5)',
          }}
        >
          <div
            className="cluster"
            style={{ gap: 'var(--space-4)', alignItems: 'flex-end', flexWrap: 'wrap' }}
          >
            <div className="field" style={{ flex: '0 0 auto' }}>
              <label htmlFor="verif-scope-filter" className="label">
                Show
              </label>
              <select
                id="verif-scope-filter"
                className="input"
                value={scope}
                onChange={(e) => setScope(e.target.value as ScopeFilter)}
              >
                {SCOPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            {/* Find one row. The archive runs 300+ deep, and finding a single
                household in it is the only reason to open the Past view. */}
            <div className="field" style={{ flex: '1 1 20rem', minWidth: '14rem' }}>
              <label htmlFor="verif-search" className="label">
                Find
              </label>
              <input
                id="verif-search"
                className="input"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Resident, address, landlord, status…"
                autoComplete="off"
              />
            </div>
          </div>
          <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>
            {visibleGroups.length} of {groups.length}{' '}
            {groups.length === 1 ? 'application' : 'applications'}
            {' · '}
            {activeCount} active · {pastCount} past
          </div>
        </div>
      )}

      {loading && <p style={{ color: 'var(--fg-muted)' }}>Loading…</p>}
      {error && (
        <div className="alert" data-tone="danger" role="alert">
          <div className="alert-body">{error}</div>
        </div>
      )}

      {!loading && !error && groups.length === 0 && (
        <div className="card" style={{ textAlign: 'center', color: 'var(--fg-muted)' }}>
          No rental verifications yet. They appear here once the watcher creates records.
        </div>
      )}

      {!loading && !error && groups.length > 0 && visibleGroups.length === 0 && (
        <div className="card" style={{ textAlign: 'center', color: 'var(--fg-muted)' }}>
          {/* When the SCOPE has rows and the search emptied it, say so — and say
              where the missing rows probably are, rather than implying none exist. */}
          {tokens.length > 0 && inScopeCount > 0 ? (
            <>
              Nothing here matches “{query.trim()}”.
              {scope !== 'all' && (
                <>
                  {' '}
                  <button
                    type="button"
                    className="btn"
                    data-variant="ghost"
                    data-size="sm"
                    onClick={() => setScope('all')}
                  >
                    Search all applications
                  </button>
                </>
              )}
            </>
          ) : scope === 'active' ? (
            'No active applications with rental verifications. Switch to “Past” or “All” to see closed-out applications.'
          ) : scope === 'past' ? (
            'No past applications. Switch to “Active” or “All”.'
          ) : (
            'No rental verifications match this filter.'
          )}
        </div>
      )}

      {!loading && visibleGroups.length > 0 && (
        <div className="table-wrap">
          <table className="table" data-zebra="true">
            <thead>
              <tr>
                <th scope="col">Property</th>
                <th scope="col">Unit</th>
                <th scope="col">Applicants</th>
                <th scope="col">Status</th>
                <th scope="col">Red flags</th>
                <th scope="col">Skipped</th>
                <th scope="col">Verifications status</th>
                <th scope="col" className="col-action" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {visibleGroups.map((g) => {
                // WEL-18: Property/Unit/Applicants/Status read like the
                // Applications tab. Fall back to the groupId when the mirror
                // didn't resolve the group, so the row still renders.
                // `??` alone was wrong here: the mirror stores a BLANK name on
                // many properties, and an empty string is not nullish — so a
                // property with a perfectly good address fell through to the
                // uuid fallback. Take the first non-blank of the four.
                const propLabel = firstText(
                  g.property?.name,
                  g.property?.address,
                  g.unit?.name,
                  g.unit?.address,
                );
                const propAddress = firstText(g.property?.address);
                const propSub = propAddress && propAddress !== propLabel ? propAddress : null;
                const unitLabel = firstText(g.unit?.name, g.unit?.address) ?? '—';
                const afTone = g.appfolioStatus
                  ? AF_STATUS_TONE[g.appfolioStatus] ?? 'neutral'
                  : 'neutral';
                const afLabel = g.appfolioStatus
                  ? AF_STATUS_LABEL[g.appfolioStatus] ?? g.appfolioStatus
                  : null;
                // Color flag: a not-clean (needsAttention) group that staff
                // haven't cleared yet gets an accessible danger left-border +
                // soft tint so it stands out in the table at a glance.
                const flagged = isFlagged(g);
                return (
                  <tr
                    key={g.groupId}
                    data-flagged={flagged ? 'true' : undefined}
                    style={
                      flagged
                        ? {
                            // Theme-aware danger tokens (work in light + dark).
                            boxShadow: 'inset 4px 0 0 0 var(--danger)',
                            background: 'var(--danger-soft)',
                          }
                        : undefined
                    }
                  >
                    <td>
                      {propLabel ? (
                        <>
                          <div style={{ fontWeight: 'var(--weight-medium)' }}>{propLabel}</div>
                          {propSub && (
                            <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-xs)' }}>
                              {propSub}
                            </div>
                          )}
                        </>
                      ) : g.applicants.length > 0 ? (
                        // Roughly two thirds of the archive applied for a
                        // property AppFolio's mirror no longer carries (WHP has
                        // since off-boarded the owner), so there is no address to
                        // print. Say so, rather than printing a uuid: the row is
                        // already identified by the people in the next column,
                        // and a uuid in the Property cell is what made this
                        // screen unreadable in the first place.
                        <span style={{ color: 'var(--fg-subtle)' }} title="This property is no longer in AppFolio">
                          —
                        </span>
                      ) : (
                        // Nothing at all resolved. The people in the next column
                        // identify the row; an id is not something anyone can use
                        // (Miles, 2026-08-29), so the cell says "no longer on file".
                        <span style={{ color: 'var(--fg-subtle)' }} title="This property is no longer on file">
                          —
                        </span>
                      )}
                    </td>
                    <td>{unitLabel}</td>
                    <td>{applicantSummary(g.applicants)}</td>
                    <td>
                      {afLabel ? (
                        <span className="pill" data-tone={afTone}>
                          {afTone === 'neutral' && <span className="dot" data-tone="muted" />}
                          {afLabel}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--fg-subtle)' }}>—</span>
                      )}
                    </td>
                    <td>
                      {g.redFlagCount > 0 ? (
                        <span className="pill" data-tone="danger">{g.redFlagCount}</span>
                      ) : (
                        <span style={{ color: 'var(--fg-subtle)' }}>0</span>
                      )}
                    </td>
                    <td>
                      {g.skippedCount > 0 ? (
                        <span className="pill" data-tone="warning">{g.skippedCount}</span>
                      ) : (
                        <span style={{ color: 'var(--fg-subtle)' }}>0</span>
                      )}
                    </td>
                    <td>
                      {g.pendingState === 'pending-scan' ? (
                        // WEL-23: active In-Review group the watcher hasn't minted
                        // records for yet — the realtime resweep will, within ~1 min.
                        <span className="pill" data-tone="info">
                          <span className="dot" data-tone="muted" />
                          Pending scan
                        </span>
                      ) : g.pendingState === 'no-history' ? (
                        // WEL-23: the watcher scanned it and found nothing to verify.
                        <span className="pill" data-tone="neutral">
                          <span className="dot" data-tone="muted" />
                          No history — nothing to verify
                        </span>
                      ) : g.needsAttention ? (
                        // Not-clean. Flagged (danger) until staff clear it;
                        // once cleared it stays "Needs review" but loses the
                        // color flag (text label "Reviewed").
                        flagged ? (
                          <span className="pill" data-tone="danger">
                            <span className="dot" data-tone="danger" />
                            Needs review
                          </span>
                        ) : (
                          <span className="cluster" style={{ gap: 'var(--space-2)', alignItems: 'center' }}>
                            <span className="pill" data-tone="warning">Needs review</span>
                            <span className="badge" data-size="sm" data-tone="success">Reviewed</span>
                          </span>
                        )
                      ) : (
                        <span className="pill" data-tone="success">Clear</span>
                      )}
                    </td>
                    <td className="col-action">
                      {/* Record-less rows have nothing to open — no Review button. */}
                      {g.pendingState ? null : (
                        <button
                          type="button"
                          className="btn"
                          data-variant="secondary"
                          data-size="sm"
                          onClick={() => setOpenGroupId(g.groupId)}
                        >
                          Review
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <VerificationDetailModal
        group={openGroup}
        open={openGroup !== null}
        onClose={() => setOpenGroupId(null)}
        onChanged={() => void load()}
        onClearFlag={optimisticallyClearFlag}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail modal — per-tenancy compare + verdict.
// ---------------------------------------------------------------------------

function VerificationDetailModal({
  group,
  open,
  onClose,
  onChanged,
  onClearFlag,
}: {
  group: VerificationGroupSummary | null;
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
  /** Optimistically de-color the row the instant staff clear the flag. */
  onClearFlag: (groupId: string) => void;
}): React.JSX.Element | null {
  const containerRef = useFocusTrap(open);
  const closeRef = useRef<HTMLButtonElement>(null);
  const [clearing, setClearing] = useState(false);
  const [clearError, setClearError] = useState<string | null>(null);

  useEscapeKey(onClose, open);
  useInertBackground(open);
  useEffect(() => {
    if (open) closeRef.current?.focus();
  }, [open]);
  // Reset the per-open clear state whenever a different group is opened.
  useEffect(() => {
    setClearError(null);
    setClearing(false);
  }, [group?.groupId]);

  if (!open || !group) return null;

  // The "Clear flag" action shows ONLY when the group is currently flagged
  // (not-clean AND not yet cleared). Living in the OPENED detail naturally
  // enforces "they've reviewed it before clearing."
  const flagged = isFlagged(group);
  const runClearFlag = async () => {
    setClearing(true);
    setClearError(null);
    // Optimistically de-color the row now; the refetch confirms it.
    onClearFlag(group.groupId);
    try {
      await clearRvFlag(group.groupId);
      onChanged();
      onClose();
    } catch (err) {
      // Roll back the optimistic clear by refetching the true server state.
      setClearError(err instanceof Error ? err.message : 'Failed to clear the flag');
      onChanged();
    } finally {
      setClearing(false);
    }
  };

  const dialog = (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="verif-modal-title"
        className="modal"
        data-size="lg"
      >
        <div className="modal-header">
          <div className="stack-sm" style={{ minWidth: 0 }}>
            <h2 id="verif-modal-title" className="modal-title">Rental verification</h2>
            <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>
              {group.total} {group.total === 1 ? 'tenancy' : 'tenancies'}
              {group.redFlagCount > 0 ? ` · ${group.redFlagCount} with red flags` : ''}
            </div>
          </div>
          <button
            ref={closeRef}
            type="button"
            className="btn"
            data-variant="ghost"
            data-size="sm"
            data-icon-only
            aria-label="Close"
            onClick={onClose}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="modal-body">
          <div className="stack">
            {group.records.map((r) => (
              <TenancyCard key={r.sk} record={r} onChanged={onChanged} />
            ))}
          </div>
        </div>
        <div className="modal-footer" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-3)' }}>
          {/* Clear-flag action: only when this group is currently flagged.
              Placing it in the opened detail enforces "they've opened the RV." */}
          {flagged ? (
            <div className="cluster" style={{ gap: 'var(--space-3)', alignItems: 'center', minWidth: 0 }}>
              <button
                type="button"
                className="btn"
                data-variant="primary"
                disabled={clearing}
                onClick={() => void runClearFlag()}
              >
                {clearing ? 'Clearing…' : 'Mark reviewed — clear flag'}
              </button>
              {clearError && (
                <span role="alert" style={{ color: 'var(--danger)', fontSize: 'var(--text-sm)' }}>
                  {clearError}
                </span>
              )}
            </div>
          ) : (
            <span />
          )}
          <button type="button" className="btn" data-variant="secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </>
  );

  return createPortal(dialog, document.body);
}

// ---------------------------------------------------------------------------
// TenancyCard — one tenancy: compare table, screening, red flags, verdict.
// ---------------------------------------------------------------------------

function TenancyCard({
  record,
  onChanged,
}: {
  record: RentalVerificationRecord;
  onChanged: () => void;
}): React.JSX.Element {
  const [busy, setBusy] = useState<null | 'verified' | 'concerns' | 'send' | 'resend' | 'edit'>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [llName, setLlName] = useState(record.landlord.name ?? '');
  const [llEmail, setLlEmail] = useState(record.landlord.email ?? '');
  const [llPhone, setLlPhone] = useState(record.landlord.phone ?? '');
  const [editResend, setEditResend] = useState(true);
  const meta = statusMeta(record.status);
  const flags = record.redFlags ?? [];
  const resp = record.response;
  const confirmed = resp?.confirmed;
  const screening = resp?.screening;

  const runVerdict = async (verdict: 'verified' | 'concerns') => {
    setBusy(verdict);
    setError(null);
    try {
      await setVerdict(record.groupId, record.sk, verdict);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set verdict');
    } finally {
      setBusy(null);
    }
  };

  const runSendSkipped = async () => {
    setBusy('send');
    setError(null);
    try {
      await sendSkipped(record.groupId, record.sk);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to override');
    } finally {
      setBusy(null);
    }
  };

  // WEL-24: resend the request to the current landlord (e.g. accidental decline).
  const runResend = async () => {
    setBusy('resend');
    setError(null);
    try {
      await resendVerification(record.groupId, record.sk);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend');
    } finally {
      setBusy(null);
    }
  };

  // WEL-24: save the corrected landlord contact (and resend unless opted out).
  const runEditLandlord = async () => {
    setBusy('edit');
    setError(null);
    try {
      const body: EditLandlordBody = { email: llEmail.trim(), resend: editResend };
      const name = llName.trim();
      const phone = llPhone.trim();
      if (name) body.name = name;
      if (phone) body.phone = phone;
      await editLandlord(record.groupId, record.sk, body);
      setEditing(false);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update landlord');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      {/* Header: address + status + applicants */}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 'var(--weight-medium)' }}>{addressLine(record)}</div>
          <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)', marginTop: 'var(--space-1)' }}>
            {record.applicantNames.join(', ') || '—'}
          </div>
        </div>
        <span className="pill" data-tone={meta.tone} style={{ alignSelf: 'flex-start' }}>{meta.label}</span>
      </div>

      {/* Skip reason + "Send anyway" override */}
      {record.status === 'skipped' && (
        <div className="alert" data-tone="warning">
          <div className="alert-body">
            <div className="alert-title">Filtered out{record.skipReason ? `: ${record.skipReason}` : ''}</div>
            {record.signals && record.signals.length > 0 && (
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', marginTop: 'var(--space-1)' }}>
                Signals: {record.signals.join('; ')}
              </div>
            )}
            <div style={{ marginTop: 'var(--space-3)' }}>
              <button
                type="button"
                className="btn"
                data-variant="secondary"
                data-size="sm"
                disabled={busy !== null}
                onClick={() => void runSendSkipped()}
              >
                {busy === 'send' ? 'Queuing…' : 'Send anyway'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Red flags */}
      {flags.length > 0 && (
        <div className="alert" data-tone="danger">
          <div className="alert-body">
            <div className="alert-title">Red flags</div>
            <ul style={{ margin: 'var(--space-2) 0 0', paddingLeft: 'var(--space-5)' }}>
              {flags.map((f, i) => (
                <li key={i} style={{ fontSize: 'var(--text-sm)' }}>{f}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Claimed vs confirmed compare */}
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Field</th>
              <th scope="col">Applicant claimed</th>
              <th scope="col">Landlord confirmed</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ color: 'var(--fg-muted)' }}>Dates</td>
              <td>{dateRange(record.claimed.residedFrom, record.claimed.residedTo, record.claimed.current)}</td>
              <td>
                {confirmed
                  ? dateRange(confirmed.moveIn, confirmed.moveOut, confirmed.stillResiding === true)
                  : '—'}
              </td>
            </tr>
            <tr>
              <td style={{ color: 'var(--fg-muted)' }}>Monthly rent</td>
              <td>{money(record.claimed.monthlyRent)}</td>
              <td>{money(confirmed?.monthlyRent)}</td>
            </tr>
            <tr>
              <td style={{ color: 'var(--fg-muted)' }}>Landlord</td>
              <td colSpan={2}>
                {record.landlord.name || '—'}
                {record.landlord.email ? ` · ${record.landlord.email}` : ''}
                {record.landlord.phone ? ` · ${record.landlord.phone}` : ''}
                {record.landlord.source !== 'provided' && (
                  <span className="badge" data-size="sm" style={{ marginLeft: 'var(--space-2)' }}>
                    {record.landlord.source}
                  </span>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* WEL-24: same-landlord dedup note (this record is, or covers, multiple tenancies). */}
      {record.dedupedToSk && (
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)' }}>
          Grouped with another tenancy for this landlord — the request is sent once (no duplicate email).
        </div>
      )}
      {record.alsoVerifyingAddresses && record.alsoVerifyingAddresses.length > 0 && (
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)' }}>
          One request to this landlord also covers: {record.alsoVerifyingAddresses.join('; ')}
        </div>
      )}

      {/* WEL-24: edit landlord (+ resend) / resend controls. */}
      {!editing ? (
        <div className="cluster" style={{ gap: 'var(--space-2)' }}>
          <button
            type="button"
            className="btn"
            data-variant="secondary"
            data-size="sm"
            disabled={busy !== null}
            onClick={() => setEditing(true)}
          >
            Edit landlord
          </button>
          <button
            type="button"
            className="btn"
            data-variant="secondary"
            data-size="sm"
            disabled={busy !== null || !record.landlord.email}
            title={record.landlord.email ? 'Re-send the verification request' : 'No landlord email on file'}
            onClick={() => void runResend()}
          >
            {busy === 'resend' ? 'Resending…' : 'Resend request'}
          </button>
        </div>
      ) : (
        <div className="card" style={{ background: 'var(--surface-2, transparent)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <div style={{ fontWeight: 'var(--weight-medium)', fontSize: 'var(--text-sm)' }}>Edit landlord contact</div>
          <div className="field">
            <label className="label" htmlFor={`ll-name-${record.sk}`}>Name</label>
            <input id={`ll-name-${record.sk}`} className="input" value={llName} onChange={(e) => setLlName(e.target.value)} placeholder="Landlord / property manager name" />
          </div>
          <div className="field">
            <label className="label" htmlFor={`ll-email-${record.sk}`}>Email</label>
            <input id={`ll-email-${record.sk}`} type="email" className="input" value={llEmail} onChange={(e) => setLlEmail(e.target.value)} placeholder="landlord@example.com" />
          </div>
          <div className="field">
            <label className="label" htmlFor={`ll-phone-${record.sk}`}>Phone</label>
            <input id={`ll-phone-${record.sk}`} className="input" value={llPhone} onChange={(e) => setLlPhone(e.target.value)} placeholder="(509) 555-0100" />
          </div>
          <label className="cluster" style={{ gap: 'var(--space-2)', fontSize: 'var(--text-sm)', alignItems: 'center' }}>
            <input type="checkbox" checked={editResend} onChange={(e) => setEditResend(e.target.checked)} />
            Re-send the verification request after saving
          </label>
          <div className="cluster" style={{ gap: 'var(--space-2)' }}>
            <button type="button" className="btn" data-variant="primary" data-size="sm" disabled={busy !== null} onClick={() => void runEditLandlord()}>
              {busy === 'edit' ? 'Saving…' : editResend ? 'Save & resend' : 'Save'}
            </button>
            <button type="button" className="btn" data-variant="ghost" data-size="sm" disabled={busy !== null} onClick={() => { setEditing(false); setError(null); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Screening answers */}
      {screening && (
        <div className="stack-sm">
          <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)', fontWeight: 'var(--weight-medium)' }}>
            Screening answers
          </div>
          <div className="table-wrap">
            <table className="table">
              <tbody>
                <ScreeningRow label="Others living there" value={screening.othersCount} />
                <ScreeningRow label="Proper notice to vacate" bool={screening.properNotice} flagWhen={false} detail={screening.properNoticeTerms} />
                <ScreeningRow label="Eviction" bool={screening.eviction} flagWhen={true} detail={screening.evictionExplain} />
                <ScreeningRow label="NSF / late payments" bool={screening.nsfOrLate} flagWhen={true} detail={screening.nsfLateDetail} />
                <ScreeningRow label="Outstanding debt" bool={screening.outstandingDebt} flagWhen={true} detail={screening.debtDetail} />
                <ScreeningRow label="Lease violations" bool={screening.leaseViolations} flagWhen={true} detail={screening.violationsExplain} />
                <ScreeningRow label="Animals" bool={screening.pets} detail={screening.petsDetail} />
                <ScreeningRow label="Other concerns" bool={screening.otherConcerns} flagWhen={true} detail={screening.otherConcernsDetail} />
                <ScreeningRow label="Would rent again" bool={screening.rentAgain} flagWhen={false} detail={screening.rentAgainWhyNot} />
              </tbody>
            </table>
          </div>
          {resp?.respondent?.name && (
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-subtle)' }}>
              Respondent: {resp.respondent.name}
              {resp.respondent.email ? ` · ${resp.respondent.email}` : ''}
            </div>
          )}
        </div>
      )}

      {error && (
        <div role="alert" style={{ color: 'var(--danger)', fontSize: 'var(--text-sm)' }}>{error}</div>
      )}

      {/* Verdict */}
      <div className="cluster" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)' }}>
          {record.verdict ? (
            <>
              Verdict:{' '}
              <span className="pill" data-tone={record.verdict === 'verified' ? 'success' : 'danger'}>
                {record.verdict === 'verified' ? 'Verified' : 'Concerns'}
              </span>
              {record.verdictBy ? <span style={{ marginLeft: 'var(--space-2)' }}>by {record.verdictBy}</span> : null}
            </>
          ) : (
            'No verdict yet'
          )}
        </div>
        <div className="cluster" role="group" aria-label="Set verdict">
          <button
            type="button"
            className="btn"
            data-variant={record.verdict === 'verified' ? 'primary' : 'secondary'}
            data-size="sm"
            disabled={busy !== null}
            onClick={() => void runVerdict('verified')}
          >
            {busy === 'verified' ? 'Saving…' : 'Verified'}
          </button>
          <button
            type="button"
            className="btn"
            data-variant={record.verdict === 'concerns' ? 'danger' : 'danger-ghost'}
            data-size="sm"
            disabled={busy !== null}
            onClick={() => void runVerdict('concerns')}
          >
            {busy === 'concerns' ? 'Saving…' : 'Concerns'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ScreeningRow({
  label,
  value,
  bool,
  flagWhen,
  detail,
}: {
  label: string;
  value?: number | undefined;
  bool?: boolean | undefined;
  /** When set, render the answer as a red flag if `bool === flagWhen`. */
  flagWhen?: boolean | undefined;
  detail?: string | undefined;
}): React.JSX.Element | null {
  let answer: React.ReactNode = '—';
  let isFlag = false;
  if (value !== undefined) {
    answer = String(value);
  } else if (bool !== undefined) {
    answer = bool ? 'Yes' : 'No';
    isFlag = flagWhen !== undefined && bool === flagWhen;
  }
  return (
    <tr>
      <td style={{ color: 'var(--fg-muted)', width: '45%' }}>{label}</td>
      <td>
        <span style={isFlag ? { color: 'var(--danger)', fontWeight: 'var(--weight-medium)' } : undefined}>
          {answer}
        </span>
        {detail ? (
          <span style={{ color: 'var(--fg-muted)', marginLeft: 'var(--space-2)', fontSize: 'var(--text-sm)' }}>
            {detail}
          </span>
        ) : null}
      </td>
    </tr>
  );
}
